import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import type {
  GetCriterionDetailsToolInput,
  GetCriterionDetailsToolOutput,
} from "@repo/app-types/entities/copilot";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconChartBar } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { SelectedPlaybookSection } from "./selectedPlaybookSection.js";

export const GetCriterionDetailsTool: ToolComponent<
  GetCriterionDetailsToolInput,
  GetCriterionDetailsToolOutput | MastraErrorToolOutput
> = (props) => {
  if (props.status.type === "running") {
    return (
      <ChatToolBase status={props.status}>
        Looking up criterion details
      </ChatToolBase>
    );
  }

  if (checkIsMastraErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while looking up criterion details
      </ChatToolBase>
    );
  }

  if (props.status.type === "complete" && props.output?.criterion) {
    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconChartBar className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={`Reviewed "${props.output.criterion.name}"`}
      >
        <CriterionDetails result={props.output} />
      </ChatToolDisclosure>
    );
  }

  return (
    <ChatToolBase status={props.status}>
      Looking up criterion details
    </ChatToolBase>
  );
};

const CriterionDetails: FC<{
  result: GetCriterionDetailsToolOutput;
}> = ({ result }) => {
  const criterion = result.criterion;

  if (!criterion) {
    return null;
  }

  const scoreDistribution = result.health_signals?.score_distribution;
  const teamDistribution = result.health_signals?.team_distribution;
  const scoreEntries = Object.entries(scoreDistribution?.values ?? {});
  const hasScoreDistribution = scoreEntries.some(
    ([, value]) => value.count > 0,
  );
  const teamBucketEntries = Object.entries(teamDistribution?.buckets ?? {});
  const hasTeamDistribution = teamBucketEntries.some(
    ([, value]) => value.count > 0,
  );

  return (
    <div className="space-y-4 text-xs text-gray-600">
      <SelectedPlaybookSection playbook={result.playbook} />
      <section className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400">Criterion</div>
        <div className="space-y-1">
          <div className="font-medium text-foreground">{criterion.name}</div>
          {criterion.section_path.length > 0 ? (
            <div className="text-muted-foreground">
              {criterion.section_path.join(" / ")}
            </div>
          ) : null}
          <div className="text-muted-foreground">
            {criterion.grading_value_type === "1_5_score"
              ? "1-5 score"
              : "Yes/no"}
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400">
          Performance
        </div>
        {scoreDistribution ? (
          <>
            <div className="grid gap-2 sm:grid-cols-3">
              <Metric
                label="Interactions"
                value={scoreDistribution.total_interactions.toLocaleString()}
              />
              <Metric
                label="Graded"
                value={scoreDistribution.graded_interactions.toLocaleString()}
              />
              <Metric
                label="Date range"
                value={formatDateRange(
                  scoreDistribution.requested_date_range.after,
                  scoreDistribution.requested_date_range.before,
                )}
                compact
              />
            </div>

            {hasScoreDistribution ? (
              <DistributionBars
                entries={scoreEntries}
                totalCount={scoreDistribution.total_interactions}
              />
            ) : (
              <div className="text-muted-foreground">
                No graded interactions found for this date range.
              </div>
            )}
          </>
        ) : (
          <div className="text-muted-foreground">
            Performance stats are not available for this criterion.
          </div>
        )}
      </section>

      {teamDistribution ? (
        <section className="space-y-2">
          <div className="text-[10px] font-semibold text-gray-400">Team</div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Metric
              label="Teammates"
              value={teamDistribution.teammate_count.toLocaleString()}
            />
            <Metric
              label="Average"
              value={formatNullableNumber(teamDistribution.average_score)}
            />
            <Metric
              label="Median"
              value={formatNullableNumber(teamDistribution.median_score)}
            />
          </div>

          {hasTeamDistribution ? (
            <DistributionBars
              entries={teamBucketEntries}
              totalCount={teamDistribution.teammate_count}
            />
          ) : null}
        </section>
      ) : null}

      {result.outcome_correlation ? (
        <section className="space-y-2">
          <div className="text-[10px] font-semibold text-gray-400">
            Outcome impact
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Metric
              label={result.outcome_correlation.rate_label}
              value={result.outcome_correlation.impact_label}
            />
            <Metric
              label="AUC"
              value={result.outcome_correlation.auc.toFixed(2)}
            />
            <Metric
              label="Grading rate"
              value={formatRate(result.outcome_correlation.grading_rate)}
            />
          </div>
          <div className="text-gray-600">
            {result.outcome_correlation.estimated_impact_sentence}
          </div>
          {result.outcome_correlation.is_high_priority ? (
            <div className="inline-flex rounded-md bg-indigo-50 px-2 py-1 text-indigo-700">
              High-priority coaching criterion
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

const Metric: FC<{
  label: string;
  value: string;
  compact?: boolean;
}> = ({ label, value, compact }) => (
  <div>
    <div className="leading-5 font-semibold text-foreground">{value}</div>
    <div
      className={
        compact ? "text-[11px] text-muted-foreground" : "text-muted-foreground"
      }
    >
      {label}
    </div>
  </div>
);

const DistributionBars: FC<{
  entries: [string, { count: number; percentage: number }][];
  totalCount: number;
}> = ({ entries, totalCount }) => (
  <div className="space-y-1.5 pt-1">
    {entries.map(([label, value]) => (
      <div
        key={label}
        className="grid grid-cols-[3rem_minmax(0,1fr)_4rem] items-center gap-2"
      >
        <div className="truncate text-gray-600">{label}</div>
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-indigo-400"
            style={{ width: `${getPercent(value.count, totalCount)}%` }}
          />
        </div>
        <div className="text-right text-foreground tabular-nums">
          {value.count.toLocaleString()}
        </div>
      </div>
    ))}
  </div>
);

function formatDateRange(after: string, before: string) {
  return `${formatDate(after)} to ${formatDate(before)}`;
}

function formatDate(value: string) {
  const dateTime = DateTime.fromISO(value);

  if (!dateTime.isValid) {
    return value;
  }

  return dateTime.toLocaleString(DateTime.DATE_MED);
}

function formatNullableNumber(value: number | null) {
  return value === null
    ? "Not available"
    : value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatRate(rate: number) {
  return new Intl.NumberFormat(undefined, {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(rate);
}

function getPercent(count: number, totalCount: number) {
  if (totalCount === 0) {
    return 0;
  }

  return Math.round((count / totalCount) * 100);
}
